import React, { useRef, useState, useEffect, useCallback } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';

/**
 * Horizontal scrolling container with visible click-to-scroll arrows and
 * edge fade indicators. Use for any strip of content (category pills, tabs,
 * thumbnails) whose full width exceeds the viewport.
 *
 * The arrows appear only when the content is actually overflowing AND there
 * is more content in that direction. Wheel-scroll horizontally is enabled so
 * mouse users can also scroll by two-finger swipe or shift+wheel.
 */
const HorizontalScroller = ({ children, className = '', arrowVariant = 'floating' }) => {
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanLeft(scrollLeft > 2);
    setCanRight(scrollLeft + clientWidth < scrollWidth - 2);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    // Also re-check after children change size (fonts load, etc.)
    const obs = 'ResizeObserver' in window ? new ResizeObserver(updateArrows) : null;
    if (obs) obs.observe(el);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
      if (obs) obs.disconnect();
    };
  }, [updateArrows, children]);

  const scrollByAmount = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(200, el.clientWidth * 0.7), behavior: 'smooth' });
  };

  const arrowBase = arrowVariant === 'floating'
    ? 'absolute top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-slate-900/90 border border-white/10 backdrop-blur text-white shadow-lg hover:bg-blue-500 hover:border-blue-500 transition-colors flex items-center justify-center'
    : 'w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 flex items-center justify-center transition-colors';

  return (
    <div className={`relative ${className}`}>
      {canLeft && (
        <button
          type="button"
          onClick={() => scrollByAmount(-1)}
          className={`${arrowBase} left-1`}
          aria-label="Scroll left"
          data-testid="hscroll-left"
        >
          <CaretLeft weight="bold" className="w-4 h-4" />
        </button>
      )}
      {canRight && (
        <button
          type="button"
          onClick={() => scrollByAmount(1)}
          className={`${arrowBase} right-1`}
          aria-label="Scroll right"
          data-testid="hscroll-right"
        >
          <CaretRight weight="bold" className="w-4 h-4" />
        </button>
      )}

      {/* Left fade */}
      {canLeft && (
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-black to-transparent z-[5]" />
      )}
      {/* Right fade */}
      {canRight && (
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-black to-transparent z-[5]" />
      )}

      <div
        ref={scrollRef}
        className="flex gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent pb-2"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgb(63 63 70) transparent',
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default HorizontalScroller;
